/**
 * User Preferences Utility
 * Handles saving and loading user preferences across different views and modes
 */

const PREFERENCES_KEY = 'cloudlabel_user_preferences';

// Default preferences
const DEFAULT_PREFERENCES = {
  widgetsAdmin: {
    viewMode: 'cards', // 'cards' or 'list'
    searchQuery: '',
    // Future settings can be added here
    // sortBy: 'name',
    // sortOrder: 'asc',
    // itemsPerPage: 20
  },
  widgetsHome: {
    viewMode: 'cards',
    searchQuery: '',
  },
  warnings: {
    hidePrinterLimitWarning: false,
    hideLabelsLimitWarning: false,
    // Future warning preferences can be added here
  },
  // Future views can be added here
  // users: {
  //   viewMode: 'table',
  //   searchQuery: '',
  // },
  // groups: {
  //   viewMode: 'cards',
  //   searchQuery: '',
  // }
};

/**
 * Get all user preferences
 * @returns {Object} User preferences object
 */
export function getUserPreferences() {
  try {
    const stored = localStorage.getItem(PREFERENCES_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to ensure all properties exist
      return mergeDeep(DEFAULT_PREFERENCES, parsed);
    }
  } catch (error) {
    console.warn('Failed to load user preferences:', error);
  }
  return DEFAULT_PREFERENCES;
}

/**
 * Save user preferences
 * @param {Object} preferences - Preferences object to save
 */
export function saveUserPreferences(preferences) {
  try {
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify(preferences));
  } catch (error) {
    console.warn('Failed to save user preferences:', error);
  }
}

/**
 * Get preferences for a specific view/mode
 * @param {string} viewKey - The view key (e.g., 'widgetsAdmin', 'widgetsHome')
 * @returns {Object} Preferences for the specific view
 */
export function getViewPreferences(viewKey) {
  const allPreferences = getUserPreferences();
  return allPreferences[viewKey] || DEFAULT_PREFERENCES[viewKey] || {};
}

/**
 * Update preferences for a specific view/mode
 * @param {string} viewKey - The view key (e.g., 'widgetsAdmin', 'widgetsHome')
 * @param {Object} viewPreferences - Preferences to update for this view
 */
export function updateViewPreferences(viewKey, viewPreferences) {
  const allPreferences = getUserPreferences();
  allPreferences[viewKey] = { ...allPreferences[viewKey], ...viewPreferences };
  saveUserPreferences(allPreferences);
}

/**
 * Reset all preferences to defaults
 */
export function resetUserPreferences() {
  try {
    localStorage.removeItem(PREFERENCES_KEY);
  } catch (error) {
    console.warn('Failed to reset user preferences:', error);
  }
}

/**
 * Deep merge two objects
 * @param {Object} target - Target object
 * @param {Object} source - Source object
 * @returns {Object} Merged object
 */
function mergeDeep(target, source) {
  const result = { ...target };
  
  for (const key in source) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      result[key] = mergeDeep(target[key] || {}, source[key]);
    } else {
      result[key] = source[key];
    }
  }
  
  return result;
}

/**
 * Get a specific preference value
 * @param {string} viewKey - The view key
 * @param {string} preferenceKey - The preference key
 * @param {*} defaultValue - Default value if not found
 * @returns {*} The preference value
 */
export function getPreference(viewKey, preferenceKey, defaultValue = null) {
  const viewPreferences = getViewPreferences(viewKey);
  return viewPreferences[preferenceKey] !== undefined ? viewPreferences[preferenceKey] : defaultValue;
}

/**
 * Set a specific preference value
 * @param {string} viewKey - The view key
 * @param {string} preferenceKey - The preference key
 * @param {*} value - The value to set
 */
export function setPreference(viewKey, preferenceKey, value) {
  const viewPreferences = getViewPreferences(viewKey);
  viewPreferences[preferenceKey] = value;
  updateViewPreferences(viewKey, viewPreferences);
}
